import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select, Spin, Empty } from 'antd';
import { debounce } from 'lodash';
import { getUserDetail, getUserPage } from '@/services/ant-design-pro/api';

export interface UserSelectProps {
  value?: number | string | { value?: string | number; label?: React.ReactNode };
  onChange?: (value?: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  /** 默认加载第一页条数 */
  pageSize?: number;
}

function normalizeUserId(value: UserSelectProps['value']): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value === 'object' && 'value' in value) {
    const id = value.value;
    return id === undefined || id === null || String(id).trim() === '' ? undefined : String(id).trim();
  }
  return String(value).trim() || undefined;
}

const UserSelect: React.FC<UserSelectProps> = ({
  value,
  onChange,
  placeholder = '请选择用户',
  allowClear = true,
  disabled = false,
  style,
  pageSize = 10,
}) => {
  const [options, setOptions] = useState<Array<{ label: string; value: string; user: any }>>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const loadedDetailIdRef = useRef<string | null>(null);

  const valueStr = normalizeUserId(value);

  const formatUserLabel = (user: { cname?: string; username?: string; id?: string | number }) => {
    const name = user.cname?.trim() || '';
    return user.username ? `${name} (${user.username})` : name || String(user.id ?? '');
  };

  const fetchUsers = useCallback(
    debounce(async (keyword: string = '') => {
      setLoading(true);
      try {
        const response = await getUserPage({
          page: 1,
          pageSize,
          cnameLike: keyword || undefined,
        });
        if (response.success && response.result?.list) {
          setOptions(
            response.result.list.map((user: any) => ({
              label: formatUserLabel(user),
              value: String(user.id),
              user,
            })),
          );
        } else {
          setOptions([]);
        }
      } catch (error) {
        console.error('加载用户列表失败:', error);
        setOptions([]);
      } finally {
        setLoading(false);
        setHasLoaded(true);
      }
    }, 300),
    [pageSize],
  );

  useEffect(() => {
    if (!valueStr) {
      loadedDetailIdRef.current = null;
      return;
    }
    if (loadedDetailIdRef.current === valueStr) {
      return;
    }
    if (options.some((opt) => opt.value === valueStr && opt.user?.cname)) {
      loadedDetailIdRef.current = valueStr;
      return;
    }
    let cancelled = false;
    getUserDetail(valueStr)
      .then((response) => {
        if (cancelled || !response.success || !response.result) {
          return;
        }
        const user = response.result;
        loadedDetailIdRef.current = valueStr;
        setOptions((prev) => {
          if (prev.some((opt) => opt.value === valueStr)) {
            return prev;
          }
          return [
            {
              label: formatUserLabel(user),
              value: String(user.id),
              user,
            },
            ...prev,
          ];
        });
      })
      .catch(() => {
        // ignore
      });
    return () => {
      cancelled = true;
    };
  }, [valueStr, options]);

  const displayOptions = useMemo(() => {
    if (valueStr && !options.some((opt) => opt.value === valueStr)) {
      return [{ label: valueStr, value: valueStr, user: { id: valueStr, cname: valueStr } }, ...options];
    }
    return options;
  }, [valueStr, options]);

  const selectedOption = displayOptions.find((opt) => opt.value === valueStr);

  return (
    <Select
      showSearch
      allowClear={allowClear}
      disabled={disabled}
      style={{ width: '100%', ...style }}
      placeholder={placeholder}
      loading={loading}
      value={
        valueStr
          ? { value: valueStr, label: selectedOption?.label ?? valueStr }
          : undefined
      }
      labelInValue
      optionLabelProp="label"
      filterOption={false}
      onSearch={(keyword) => fetchUsers(keyword)}
      onDropdownVisibleChange={(open) => {
        if (open && !hasLoaded) {
          fetchUsers('');
        }
      }}
      onChange={(selected) => {
        const id = selected?.value != null ? String(selected.value).trim() : undefined;
        onChange?.(id || undefined);
      }}
      onClear={() => {
        setHasLoaded(false);
        onChange?.(undefined);
      }}
      notFoundContent={
        loading ? (
          <Spin size="small" />
        ) : hasLoaded ? (
          <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div style={{ textAlign: 'center', color: '#999', padding: '8px 0' }}>请输入姓名搜索</div>
        )
      }
      options={displayOptions.map((opt) => ({
        label: opt.label,
        value: opt.value,
      }))}
    />
  );
};

export default UserSelect;
